import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import configServ from '../../../../services/config';
import CheckAndReturn from '../../../../utility/CheckAndReturn';

const PurchaseOrderWorkflowPopup = ({ open, onClose, poId, poRow = null, stage, allowReject = false, onSaved }) => {
  const { user_id, company_id } = useSelector((state) => state.GlobalVariables);
  const [remark, setRemark] = useState('');
  const [documentFile, setDocumentFile] = useState(null);
  const [selectedStage, setSelectedStage] = useState(stage);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [poAssignTo, setPoAssignTo] = useState(poRow?.po_assigned_to || null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await configServ.getUserAdmin({ company_id });
        if (res.status === 200 && Array.isArray(res.data)) {
          setUserOptions(res.data.map((u) => ({ label: u.name || u.fname || u.username || u.email, value: u.id })));
        }
      } catch (e) {
        console.log(e);
      }
    };
    fetchUsers();
  }, [company_id]);

  useEffect(() => {
    if (!open) {
      setRemark('');
      setDocumentFile(null);
      setErrorMessage('');
      setSaving(false);
      setSelectedStage(stage);
    } else {
      setSelectedStage(stage);
    }
  }, [open, stage]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setDocumentFile(file ?? null);
  };

  const handleSubmit = async () => {
    if (!poId || !user_id) {
      setErrorMessage('Unable to identify purchase order or logged in user.');
      return;
    }

    setSaving(true);
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('po_id', poId);
      formData.append('user_id', user_id);
      formData.append('stage', selectedStage);
      formData.append('company_id', company_id);
      formData.append('remark', remark ?? '');

      // If assignment made on recheck, include assignment fields
      if (Number(stage) === 1 && poAssignTo) {
        formData.append('po_assigned_to', poAssignTo);
        formData.append('po_assigned_on', 2); // 2 = on recheck
        formData.append('po_assigned_on_recheck_time', new Date().toISOString());
      }

      if (documentFile) {
        formData.append('document', documentFile);
      }

      const result = await configServ.savePurchaseOrderWorkflow(formData);
      if (result?.status === 200) {
        onSaved?.();
        onClose?.();
      } else {
        setErrorMessage(
          CheckAndReturn.formatApiErrorMessage(
            result?.message,
            'Failed to save workflow.'
          )
        );
      }
    } catch (error) {
      setErrorMessage(
        CheckAndReturn.formatApiErrorMessage(
          error?.response?.data?.message || error?.message,
          'Failed to save workflow.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const stageLabel = selectedStage === 1 ? 'Recheck' : selectedStage === 2 ? 'Verify' : selectedStage === 3 ? 'Approve' : selectedStage === 4 || selectedStage === 5 ? 'Reject' : 'Save';
  const isVerifyMode = stage === 2 || stage === '2';
  const isApproveMode = stage === 3 || stage === '3';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{stageLabel} Purchase Order</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {allowReject && (isVerifyMode || isApproveMode) && (
            <RadioGroup
              row
              value={selectedStage?.toString()}
              onChange={(e) => setSelectedStage(Number(e.target.value))}
              sx={{ gap: 2 }}
            >
              <FormControlLabel
                value={isVerifyMode ? '2' : '3'}
                control={<Radio />}
                label={isVerifyMode ? 'Verify' : 'Approve'}
              />
              <FormControlLabel value="4" control={<Radio />} label="Reject" />
            </RadioGroup>
          )}

          {/* Show PO Assign to on Recheck (stage 1) only if PO wasn't assigned on create */}
          {Number(stage) === 1 && (!poRow || !poRow.po_assigned_on || Number(poRow.po_assigned_on) !== 1) && (
            <TextField
              shrink
              select
              label="PO Assign to"
              variant="outlined"
              fullWidth
              size="small"
              value={poAssignTo || ''}
              onChange={(e) => setPoAssignTo(e.target.value ? Number(e.target.value) : null)}
              SelectProps={{ native: true }}
            >
              <option value="">None</option>
              {userOptions.map((u) => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </TextField>
          )}
          <TextField
            label="Remark"
            multiline
            minRows={3}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            fullWidth
          />
          <Button variant="outlined" component="label" sx={{ textTransform: 'none' }}>
            {documentFile ? 'Change Document' : 'Upload Document'}
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={handleFileChange}
            />
          </Button>
          {documentFile && (
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              Selected file: {documentFile.name}
            </Typography>
          )}
          {errorMessage && (
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving...' : stageLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseOrderWorkflowPopup;
